import React, { useEffect, useRef, useState } from 'react';
import { Badge, Card, Table } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import { formatAnswerDay } from '@/shared/utils/deadline';
import { PHONE_QUERY } from '@/shared/utils/tableControls';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import LoadError from '@/components/common/LoadError';
import './UnsubmittedDrafts.css';

export const DRAFTS_SECTION_ID = 'unsubmitted-drafts';

const courseText = (draft) =>
    draft.course
        ? `${draft.course.subject} ${draft.course.courseNumber}${
              draft.sectionNumber ? ` section ${draft.sectionNumber}` : ''
          }`
        : 'Course not chosen yet';

const progressOf = (draft) =>
    draft.complete ? 'All sections filled in' : 'Started';

function Progress({ draft }) {
    return (
        <Badge bg={draft.complete ? 'success' : 'secondary'} pill>
            {progressOf(draft)}
        </Badge>
    );
}

// Rows have no id: a draft is not the coordinator's to open, so there is
// nothing to link to or key on beyond its place in the list.
function DraftsTable({ drafts }) {
    return (
        <Table responsive hover className="mb-0">
            <thead className="bg-light">
                <tr>
                    <th scope="col">Course</th>
                    <th scope="col">Faculty member</th>
                    <th scope="col">Last saved</th>
                    <th scope="col">Progress</th>
                </tr>
            </thead>
            <tbody>
                {drafts.map((draft, index) => (
                    <tr key={index}>
                        <td>
                            <span className="fw-semibold">
                                {courseText(draft)}
                            </span>
                            {draft.course?.title && (
                                <small className="d-block text-muted">
                                    {draft.course.title}
                                </small>
                            )}
                        </td>
                        <td>
                            <div>{draft.facultyName}</div>
                            <a href={`mailto:${draft.facultyEmail}`}>
                                {draft.facultyEmail}
                            </a>
                        </td>
                        <td>{formatAnswerDay(draft.lastSaved)}</td>
                        <td>
                            <Progress draft={draft} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

function DraftsCards({ drafts }) {
    return (
        <ul
            className="list-unstyled mb-0"
            aria-label="Started but not submitted"
        >
            {drafts.map((draft, index) => (
                <li key={index} className="border-bottom px-3 py-3">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <span className="fw-semibold">{courseText(draft)}</span>
                        <Progress draft={draft} />
                    </div>
                    {draft.course?.title && (
                        <div className="small text-body-secondary">
                            {draft.course.title}
                        </div>
                    )}
                    <div className="mt-1">{draft.facultyName}</div>
                    <a href={`mailto:${draft.facultyEmail}`}>
                        {draft.facultyEmail}
                    </a>
                    <div className="small text-body-secondary">
                        Last saved {formatAnswerDay(draft.lastSaved)}
                    </div>
                </li>
            ))}
        </ul>
    );
}

/**
 * "Started but not submitted (N)": the requests faculty have begun and not yet
 * submitted, below a coordinator's Requests table. Collapsed until opened, and
 * opened (and focused) when the Overview's link names it with `forceOpen`.
 * Read-only: a draft is the faculty member's own, so a coordinator sees that
 * it exists and who to nudge, never its answers, and cannot open it. Renders
 * nothing when there are none.
 */
export default function UnsubmittedDrafts({ programType, term, forceOpen }) {
    const [state, setState] = useState({ status: 'loading' });
    const [open, setOpen] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const summary = useRef(null);
    const phone = useMediaQuery(PHONE_QUERY);

    useEffect(() => {
        if (!term) return undefined;
        let cancelled = false;
        setState({ status: 'loading' });
        applicationService
            .getProgramDrafts(programType, term)
            .then((drafts) => {
                if (!cancelled) setState({ status: 'ready', drafts });
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'error' });
            });
        return () => {
            cancelled = true;
        };
    }, [programType, term, attempt]);

    const ready = state.status === 'ready' && state.drafts.length > 0;
    useEffect(() => {
        if (!forceOpen || !ready) return;
        setOpen(true);
        summary.current?.scrollIntoView?.({ block: 'start' });
        summary.current?.focus();
    }, [forceOpen, ready]);

    if (state.status === 'error') {
        return (
            <LoadError
                what="the requests started but not submitted"
                className="mt-4"
                onRetry={() => setAttempt((n) => n + 1)}
            />
        );
    }
    if (!ready) return null;

    const { drafts } = state;
    return (
        <Card
            id={DRAFTS_SECTION_ID}
            className="border-0 shadow-sm mt-4"
            aria-label="Started but not submitted"
        >
            <details
                open={open}
                onToggle={(event) => setOpen(event.currentTarget.open)}
            >
                <summary
                    ref={summary}
                    className="unsubmitted-drafts-summary px-3 py-3 fw-semibold"
                >
                    {`Started but not submitted (${drafts.length})`}
                </summary>
                <Card.Body className="p-0">
                    <p className="px-3 pt-2 pb-2 mb-0 small text-muted">
                        Faculty have started these requests and not submitted
                        them yet. You can see that they exist, not their
                        answers, and can write to the faculty member to help
                        them finish.
                    </p>
                    {phone ? (
                        <DraftsCards drafts={drafts} />
                    ) : (
                        <DraftsTable drafts={drafts} />
                    )}
                </Card.Body>
            </details>
        </Card>
    );
}
